import type { DrugSortKey } from '@/entities/drug/api/drug.api';
import { useDrugSearch } from '@/entities/drug/hooks/useDrugQueries';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import { ErrorState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { useState } from 'react';
import { DrugEmptyView } from './DrugEmptyView';
import { DrugFilterRail } from './DrugFilterRail';
import { countFilters, EMPTY_FILTER, type DrugFilterValue } from './drugFilter';
import { DrugResultCard } from './DrugResultCard';
import { DrugSortMenu } from './DrugSortMenu';

const PAGE_SIZE = 20;

/**
 * M-11 검색 결과 (Figma 804:4503) — 제목 "검색 결과 ‘키워드’ N건" + 정렬 / 필터(280) + 결과 카드 목록(간격 8).
 * 결과 없음(886:13011)은 필터 없이 가운데 안내만 보여 준다. 필터를 건 상태에서 0건이면 필터를 남겨 풀 수 있게 한다.
 * 상태는 모두 URL에 있고, 이 컴포넌트는 값을 받아 그리고 바뀐 값을 올려보낸다.
 */
export const DrugSearchResults = ({
  keyword,
  filter,
  sort,
  page,
  openedDrug,
  onFilterChange,
  onSortChange,
  onPageChange,
}: {
  keyword: string;
  filter: DrugFilterValue;
  sort: DrugSortKey;
  /** 1부터 */
  page: number;
  openedDrug?: string;
  onFilterChange: (next: DrugFilterValue) => void;
  onSortChange: (sort: DrugSortKey) => void;
  onPageChange: (page: number) => void;
}) => {
  const { open } = useDrugDrawer();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState(filter);

  const { data, isPending, isError, isFetching, refetch } = useDrugSearch({
    keyword,
    isDrugTradable: filter.tradable || undefined,
    filters: filter.filters.length ? filter.filters : undefined,
    pharmaCompanyIds: filter.pharma.length ? filter.pharma : undefined,
    sortKey: sort,
    page: page - 1,
    size: PAGE_SIZE,
  });

  const filterCount = countFilters(filter);
  const totalPages = data
    ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE))
    : 1;
  // 결과 없음 화면(886:13011) — 필터도 정렬도 없이 안내만
  const noResult = !!data && data.totalCount === 0 && filterCount === 0;

  const openSheet = () => {
    setDraft(filter);
    setSheetOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h1 className="flex min-w-0 flex-wrap items-center gap-x-2 text-heading-24-b">
          <span className="min-w-0 break-all text-fg-primary">
            검색 결과 ‘{keyword}’
          </span>
          {data && (
            <span className="text-fg-accent">
              {data.totalCount.toLocaleString('ko-KR')}건
            </span>
          )}
        </h1>
        {!noResult && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="medium"
              onClick={openSheet}
              className="lg:hidden"
            >
              필터{filterCount > 0 && ` ${filterCount}`}
            </Button>
            <DrugSortMenu value={sort} onChange={onSortChange} />
          </div>
        )}
      </div>

      {isError ? (
        <div className="rounded-20 bg-surface-white">
          <ErrorState onRetry={() => void refetch()} />
        </div>
      ) : noResult ? (
        <DrugEmptyView
          className="min-h-[480px] py-10"
          title="검색 결과가 없어요."
          description="다른 키워드로 검색해보세요."
        />
      ) : (
        <div className="flex items-start gap-4">
          {/* 데스크톱: 왼쪽 필터 — 고르는 즉시 반영 */}
          <aside className="sticky top-6 hidden w-[280px] shrink-0 rounded-20 bg-surface-white p-6 lg:block">
            <DrugFilterRail
              keyword={keyword}
              value={filter}
              onChange={onFilterChange}
            />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {isPending ? (
              Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-[126px] rounded-20" />
              ))
            ) : data.content.length === 0 ? (
              <DrugEmptyView
                className="rounded-20 bg-surface-white py-16"
                title="검색 결과가 없어요."
                description="필터를 바꾸거나 다른 키워드로 검색해보세요."
                action={
                  <Button
                    variant="grey"
                    size="medium"
                    onClick={() => onFilterChange(EMPTY_FILTER)}
                  >
                    필터 초기화
                  </Button>
                }
              />
            ) : (
              <>
                <ul
                  className={cn(
                    'flex flex-col gap-2 transition-opacity',
                    isFetching && 'opacity-60',
                  )}
                >
                  {data.content.map((d) => (
                    <li key={d.drugCode}>
                      <DrugResultCard
                        drug={d}
                        active={openedDrug === d.drugCode}
                        onOpen={() => void open(d.drugCode)}
                      />
                    </li>
                  ))}
                </ul>
                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={onPageChange}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* 모바일(<1024): 하단 Sheet — [필터 적용]을 눌러야 반영 (README §8) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" overlay="popup" className="lg:hidden">
          <SheetTitle className="sr-only">필터</SheetTitle>
          <SheetDescription className="sr-only">
            검색 결과 필터를 고르고 필터 적용을 누르세요.
          </SheetDescription>
          <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4">
            <DrugFilterRail
              keyword={keyword}
              value={draft}
              onChange={setDraft}
            />
          </div>
          <SheetFooter>
            <Button
              variant="grey"
              size="giant"
              onClick={() => setSheetOpen(false)}
            >
              취소
            </Button>
            <Button
              size="giant"
              onClick={() => {
                onFilterChange(draft);
                setSheetOpen(false);
              }}
            >
              필터 적용
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

/**
 * 페이지 번호 — 디자인에 페이지네이션이 없어(M-11 확인 필요) 토큰으로만 구성한다. 현재 페이지 기준 최대 5개.
 */
const Pagination = ({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) => {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from(
    { length: Math.min(5, totalPages) },
    (_, i) => start + i,
  );

  return (
    <nav
      aria-label="페이지"
      className="flex items-center justify-center gap-1 pt-4"
    >
      <Button
        variant="text"
        size="medium"
        aria-label="이전 페이지"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        leftIcon={<Icon name="nav-arrow-left-line-16" />}
      />
      {pages.map((p) => (
        <Button
          key={p}
          variant={p === page ? 'primary' : 'text'}
          size="medium"
          aria-current={p === page ? 'page' : undefined}
          onClick={() => onChange(p)}
          className="w-10"
        >
          {p}
        </Button>
      ))}
      <Button
        variant="text"
        size="medium"
        aria-label="다음 페이지"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        leftIcon={<Icon name="nav-arrow-right-line-16" />}
      />
    </nav>
  );
};
